// Contrast utilities for accessibility. Accepts both token strings
// ("262 100% 84%") and CSS function strings ("hsl(262, 100%, 84%)").
const ACCESSIBLE_LIGHT = '0 0% 98%';
const ACCESSIBLE_DARK = '0 0% 8%';
const DEFAULT_MIN_TEXT_CONTRAST = 4.5;
const SURFACE_MUTED_MIX_RATIOS = [0.42, 0.3, 0.2, 0.1, 0];
const TRANSPARENT_COLOR = { r: 0, g: 0, b: 0, a: 0 };
const DEFAULT_FALLBACK_BACKGROUND = { r: 255, g: 255, b: 255, a: 1 };

type ParsedHsl = {
  h: number;
  s: number;
  l: number;
};

type RgbaColor = {
  r: number;
  g: number;
  b: number;
  a: number;
};

export type SurfaceContrastColors = {
  foreground: string;
  mutedForeground: string;
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function toParsedHsl(value: string): ParsedHsl {
  const [h, s, l] = parseHSL(value);

  return {
    h: ((h % 360) + 360) % 360,
    s: clamp(s, 0, 100),
    l: clamp(l, 0, 100),
  };
}

function formatHslToken({ h, s, l }: ParsedHsl): string {
  return `${Math.round(((h % 360) + 360) % 360)} ${Math.round(clamp(s, 0, 100))}% ${Math.round(clamp(l, 0, 100))}%`;
}

function mixHslColors(colorA: string, colorB: string, ratioToB: number): string {
  const a = toParsedHsl(colorA);
  const b = toParsedHsl(colorB);
  const weight = clamp(ratioToB, 0, 1);
  const delta = ((((b.h - a.h) % 360) + 540) % 360) - 180;

  return formatHslToken({
    h: a.h + delta * weight,
    s: a.s + (b.s - a.s) * weight,
    l: a.l + (b.l - a.l) * weight,
  });
}

function clampColorChannel(value: number): number {
  return Math.round(clamp(value, 0, 255));
}

function parseHexColor(value: string): RgbaColor | null {
  const hex = value.replace('#', '').trim();

  if (![3, 4, 6, 8].includes(hex.length)) {
    return null;
  }

  const normalized = hex.length <= 4
    ? hex.split('').map((char) => char + char).join('')
    : hex;
  const alpha = normalized.length === 8 ? parseInt(normalized.slice(6, 8), 16) / 255 : 1;

  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
    a: clamp(alpha, 0, 1),
  };
}

function parseCssChannel(value: string, scale: number): number {
  if (value.endsWith('%')) {
    return (parseFloat(value) / 100) * scale;
  }

  return parseFloat(value);
}

function rgbaToCssColor(color: RgbaColor): string {
  return `rgb(${clampColorChannel(color.r)}, ${clampColorChannel(color.g)}, ${clampColorChannel(color.b)})`;
}

function rgbToHslToken(r: number, g: number, b: number): string {
  const red = clamp(r, 0, 255) / 255;
  const green = clamp(g, 0, 255) / 255;
  const blue = clamp(b, 0, 255) / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const lightness = (max + min) / 2;
  const delta = max - min;

  let hue = 0;
  let saturation = 0;

  if (delta !== 0) {
    saturation = lightness > 0.5
      ? delta / (2 - max - min)
      : delta / (max + min);

    switch (max) {
      case red:
        hue = (green - blue) / delta + (green < blue ? 6 : 0);
        break;
      case green:
        hue = (blue - red) / delta + 2;
        break;
      default:
        hue = (red - green) / delta + 4;
        break;
    }

    hue /= 6;
  }

  return `${Math.round(hue * 360)} ${Math.round(saturation * 100)}% ${Math.round(lightness * 100)}%`;
}

function compositeColors(foreground: RgbaColor, background: RgbaColor): RgbaColor {
  const alpha = foreground.a + background.a * (1 - foreground.a);

  if (alpha <= 0) {
    return { ...TRANSPARENT_COLOR };
  }

  return {
    r: (((foreground.r * foreground.a) + (background.r * background.a * (1 - foreground.a))) / alpha),
    g: (((foreground.g * foreground.a) + (background.g * background.a * (1 - foreground.a))) / alpha),
    b: (((foreground.b * foreground.a) + (background.b * background.a * (1 - foreground.a))) / alpha),
    a: alpha,
  };
}

export function parseHSL(hsl: string): [number, number, number] {
  const matches = (hsl || '').match(/-?\d+(?:\.\d+)?/g) || [];
  const [rawH = '0', rawS = '0', rawL = '0'] = matches;
  return [parseFloat(rawH) || 0, parseFloat(rawS) || 0, parseFloat(rawL) || 0];
}

export function parseCssColor(color: string): RgbaColor | null {
  const normalized = color.trim().toLowerCase();

  if (!normalized || normalized === 'transparent') {
    return { ...TRANSPARENT_COLOR };
  }

  if (normalized.startsWith('#')) {
    return parseHexColor(normalized);
  }

  const matches = normalized.match(/-?\d*\.?\d+%?/g);
  if (!matches || matches.length < 3) {
    return null;
  }

  if (normalized.startsWith('rgb')) {
    const [rawR = '0', rawG = '0', rawB = '0', rawA = '1'] = matches;

    return {
      r: clampColorChannel(parseCssChannel(rawR, 255)),
      g: clampColorChannel(parseCssChannel(rawG, 255)),
      b: clampColorChannel(parseCssChannel(rawB, 255)),
      a: clamp(parseCssChannel(rawA, 1), 0, 1),
    };
  }

  if (normalized.startsWith('hsl')) {
    const [h, s, l] = parseHSL(normalized);
    const [r, g, b] = hslToRgb(h, s, l);
    const rawAlpha = matches[3];

    return {
      r,
      g,
      b,
      a: clamp(rawAlpha ? parseCssChannel(rawAlpha, 1) : 1, 0, 1),
    };
  }

  return null;
}

export function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  s /= 100;
  l /= 100;
  
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = l - c / 2;
  
  let r = 0, g = 0, b = 0;
  
  if (0 <= h && h < 60) {
    r = c; g = x; b = 0;
  } else if (60 <= h && h < 120) {
    r = x; g = c; b = 0;
  } else if (120 <= h && h < 180) {
    r = 0; g = c; b = x;
  } else if (180 <= h && h < 240) {
    r = 0; g = x; b = c;
  } else if (240 <= h && h < 300) {
    r = x; g = 0; b = c;
  } else if (300 <= h && h < 360) {
    r = c; g = 0; b = x;
  }
  
  return [
    Math.round((r + m) * 255),
    Math.round((g + m) * 255),
    Math.round((b + m) * 255)
  ];
}

export function getRelativeLuminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r, g, b].map(c => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

export function getContrastRatio(color1: string, color2: string): number {
  const [h1, s1, l1] = parseHSL(color1);
  const [h2, s2, l2] = parseHSL(color2);
  
  const [r1, g1, b1] = hslToRgb(h1, s1, l1);
  const [r2, g2, b2] = hslToRgb(h2, s2, l2);
  
  const lum1 = getRelativeLuminance(r1, g1, b1);
  const lum2 = getRelativeLuminance(r2, g2, b2);
  
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  
  return (lighter + 0.05) / (darker + 0.05);
}

export function getAccessibleForeground(
  background: string,
  preferredText?: string,
  minimumContrast: number = DEFAULT_MIN_TEXT_CONTRAST,
): string {
  if (preferredText && getContrastRatio(background, preferredText) >= minimumContrast) {
    return preferredText;
  }

  const lightContrast = getContrastRatio(background, ACCESSIBLE_LIGHT);
  const darkContrast = getContrastRatio(background, ACCESSIBLE_DARK);

  return lightContrast >= darkContrast ? ACCESSIBLE_LIGHT : ACCESSIBLE_DARK;
}

export function getReadableMutedForeground(
  background: string,
  preferredText?: string,
  minimumContrast: number = DEFAULT_MIN_TEXT_CONTRAST,
): string {
  const baseForeground = getAccessibleForeground(background, preferredText, minimumContrast);

  for (const ratio of SURFACE_MUTED_MIX_RATIOS) {
    const candidate = mixHslColors(baseForeground, background, ratio);
    if (getContrastRatio(background, candidate) >= minimumContrast) {
      return candidate;
    }
  }

  return baseForeground;
}

export function getSurfaceContrastColors(
  background: string,
  preferredText?: string,
  minimumContrast: number = DEFAULT_MIN_TEXT_CONTRAST,
): SurfaceContrastColors {
  const foreground = getAccessibleForeground(background, preferredText, minimumContrast);

  return {
    foreground,
    mutedForeground: getReadableMutedForeground(background, foreground, minimumContrast),
  };
}

export function getRuntimeSurfaceContrastColors(
  cssBackgroundColor: string,
  preferredText?: string,
  minimumContrast: number = DEFAULT_MIN_TEXT_CONTRAST,
): SurfaceContrastColors {
  const parsedBackground = parseCssColor(cssBackgroundColor);
  const opaqueBackground = parsedBackground
    ? compositeColors(parsedBackground, DEFAULT_FALLBACK_BACKGROUND)
    : DEFAULT_FALLBACK_BACKGROUND;
  const backgroundToken = rgbToHslToken(
    opaqueBackground.r,
    opaqueBackground.g,
    opaqueBackground.b,
  );

  return getSurfaceContrastColors(backgroundToken, preferredText, minimumContrast);
}

export function resolveElementBackgroundColor(element: HTMLElement | null): string {
  if (!element || typeof window === 'undefined') {
    return rgbaToCssColor(DEFAULT_FALLBACK_BACKGROUND);
  }

  let current: HTMLElement | null = element;
  let resolvedColor: RgbaColor = { ...TRANSPARENT_COLOR };

  while (current) {
    const currentBackground = parseCssColor(window.getComputedStyle(current).backgroundColor) || TRANSPARENT_COLOR;
    resolvedColor = compositeColors(resolvedColor, currentBackground);

    if (resolvedColor.a >= 0.999) {
      break;
    }

    current = current.parentElement;
  }

  return rgbaToCssColor(compositeColors(resolvedColor, DEFAULT_FALLBACK_BACKGROUND));
}

export function toSurfaceContrastVariables(colors: SurfaceContrastColors): Record<string, string> {
  return {
    '--surface-fg': colors.foreground,
    '--surface-muted-fg': colors.mutedForeground,
  };
}

export function ensureAccessibleContrast(bgColor: string, textColor: string, minRatio: number = 4.5): string {
  const currentRatio = getContrastRatio(bgColor, textColor);
  
  if (currentRatio >= minRatio) {
    return textColor;
  }
  
  // If contrast is insufficient, return a high contrast alternative
  const [, , l] = parseHSL(bgColor);
  
  // If background is light (l > 40), use very dark text
  // If background is dark (l <= 40), use very light text
  if (l > 40) {
    return '0 0% 5%'; // Very dark for better contrast
  } else {
    return '0 0% 100%'; // Pure white for better contrast
  }
}

// Helper function to get high contrast color for any background
export function getHighContrastText(bgColor: string): string {
  const [, , l] = parseHSL(bgColor);
  
  // More aggressive threshold for better contrast
  if (l > 35) {
    return '0 0% 5%'; // Very dark text on light backgrounds
  } else {
    return '0 0% 100%'; // White text on dark backgrounds
  }
}
